import { AxiosResponse } from 'axios';

import { ApiVersion } from '@/config';
import { IDomesticIncidentReportRecord } from '@/models';

import { http } from './http';
import { IGetParams, IPaginatedListResponse } from './types';

export const DomesticIncidentReportApi = {
  list: async (
    params: IGetParams,
  ): Promise<
    AxiosResponse<IPaginatedListResponse<IDomesticIncidentReportRecord>>
  > => {
    return http.get(`api/${ApiVersion.V1}/domestic-incident-reports`, {
      params,
    });
  },

  getOne: async (
    id: string,
  ): Promise<AxiosResponse<IDomesticIncidentReportRecord>> => {
    return http.get(`api/${ApiVersion.V1}/domestic-incident-reports/${id}`);
  },

  create: async (
    data: Record<string, unknown>,
  ): Promise<AxiosResponse<IDomesticIncidentReportRecord>> => {
    return http.post(`api/${ApiVersion.V1}/domestic-incident-reports`, data);
  },

  update: async (
    id: string,
    data: Record<string, unknown>,
  ): Promise<AxiosResponse<IDomesticIncidentReportRecord>> => {
    return http.patch(
      `api/${ApiVersion.V1}/domestic-incident-reports/${id}`,
      data,
    );
  },

  delete: async (id: string, reason: string): Promise<AxiosResponse<void>> => {
    return http.delete(`api/${ApiVersion.V1}/domestic-incident-reports/${id}`, {
      data: { reason },
    });
  },

  listByKase: async (
    kaseId: string,
  ): Promise<AxiosResponse<IDomesticIncidentReportRecord[]>> => {
    return http.get(
      `api/${ApiVersion.V1}/domestic-incident-reports/by-kase/${kaseId}`,
    );
  },

  print: async (
    id: string,
    data: Record<string, unknown>,
  ): Promise<AxiosResponse<Blob>> => {
    return http.post(
      `api/${ApiVersion.V1}/domestic-incident-reports/${id}/print`,
      { ...data, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
      { responseType: 'blob' },
    );
  },

  availableCases: async (search?: string): Promise<AxiosResponse<any[]>> => {
    return http.get(
      `api/${ApiVersion.V1}/domestic-incident-reports/available-cases`,
      { params: search ? { search } : {} },
    );
  },

  printFromRecord: async (
    id: string,
    action: 'REDACTED' | 'UNREDACTED',
    includeHelpSheet: boolean,
  ): Promise<AxiosResponse<Blob>> => {
    return http.post(
      `api/${ApiVersion.V1}/domestic-incident-reports/${id}/print`,
      { action, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
      {
        responseType: 'blob',
        params: {
          fromRecord: 'true',
          includeHelpSheet: String(includeHelpSheet),
        },
      },
    );
  },

  downloadLatestPrint: async (
    id: string,
  ): Promise<AxiosResponse<{ url: string; fileName: string }>> => {
    return http.get(
      `api/${ApiVersion.V1}/domestic-incident-reports/${id}/download`,
    );
  },
};
